import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, User, Tag, ArrowRight, ArrowLeft, Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    if (slug && posts.length > 0) {
      fetchPost(slug);
    } else {
      setSelectedPost(null);
    }
  }, [slug, posts]);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/blog/posts?limit=20`);
      setPosts(res.data.posts || []);
    } catch { }
    setLoading(false);
  };

  const fetchPost = async (s) => {
    try {
      const res = await axios.get(`${API}/blog/posts/${s}`);
      setSelectedPost(res.data);
    } catch { }
  };

  const filtered = posts.filter(p =>
    !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.category.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (selectedPost) {
    return (
      <div style={{ paddingTop: '72px' }}>
        <section className="py-14 px-6" style={{ background: '#0B1F3A' }}>
          <div className="max-w-3xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 text-blue-300 hover:text-white text-sm mb-6 transition-colors duration-150">
              <ArrowLeft size={16} /> Back to Blog
            </Link>
            <div className="label-uppercase mb-3" style={{ color: '#D4AF37' }}>{selectedPost.category}</div>
            <h1 className="font-heading text-4xl font-bold text-white tracking-tight mb-4">{selectedPost.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-blue-300 text-sm">
              <div className="flex items-center gap-1.5"><User size={14} />{selectedPost.author}</div>
              <div className="flex items-center gap-1.5"><Calendar size={14} />{formatDate(selectedPost.published_at)}</div>
              {selectedPost.tags?.map(tag => (
                <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-xs"><Tag size={11} />{tag}</span>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 px-6 bg-white">
          <div className="max-w-3xl mx-auto">
            {selectedPost.featured_image && (
              <img
                src={selectedPost.featured_image}
                alt={selectedPost.title}
                className="w-full h-72 object-cover rounded-2xl mb-10 shadow-card"
              />
            )}
            <div
              className="prose prose-lg max-w-none"
              style={{ color: '#0F172A' }}
              dangerouslySetInnerHTML={{ __html: selectedPost.content }}
            />
            <div className="mt-16 p-8 rounded-2xl" style={{ background: '#F8FAFC', borderLeft: '4px solid #0B1F3A' }}>
              <p className="text-sm font-semibold text-[#0B1F3A] mb-2">Have tax questions?</p>
              <p className="text-[#475569] text-sm mb-4">Our team at MaTaxPreparer LLC is here to help. Book a free consultation today.</p>
              <Link to="/book-appointment">
                <button className="btn-primary text-sm px-6 py-2.5">Book Free Consultation <ArrowRight size={16} /></button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '72px' }}>
      {/* Hero */}
      <section className="py-16 px-6" style={{ background: '#0B1F3A' }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="label-uppercase mb-4" style={{ color: '#D4AF37' }}>Tax Insights</div>
          <h1 className="font-heading text-4xl font-extrabold text-white tracking-tight mb-4">
            The MaTaxPreparer Blog
          </h1>
          <p className="text-blue-200 text-lg mb-8">
            Expert insights on taxes, deductions, and financial strategy — written by our tax professionals.
          </p>
          <div className="max-w-md mx-auto relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles..."
              className="w-full h-12 pl-11 pr-4 bg-white rounded-xl border border-gray-200 text-sm outline-none focus:border-[#0B1F3A] transition-all duration-200"
              data-testid="blog-search-input"
            />
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-[#94A3B8]">Loading articles...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-[#94A3B8]">No articles found.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                  <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-floating transition-all duration-300 hover:-translate-y-1 h-full flex flex-col" data-testid="blog-post-card">
                    {post.featured_image && (
                      <img src={post.featured_image} alt={post.title} className="w-full h-48 object-cover" />
                    )}
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="label-uppercase text-[#2563EB] mb-2">{post.category}</div>
                      <h2 className="font-heading font-bold text-[#0B1F3A] text-xl leading-snug mb-3 group-hover:text-[#2563EB] transition-colors duration-200 flex-1">
                        {post.title}
                      </h2>
                      <p className="text-[#475569] text-sm leading-relaxed mb-4 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-[#94A3B8]">
                        <div className="flex items-center gap-1.5">
                          <User size={12} />{post.author}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Calendar size={12} />{formatDate(post.published_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
